import { ApplicationDetails } from '@sec-loans-ui/common/steps/loan-catalog-step/types'
import {
  ApplicationsMetaData,
  ApplicationType,
  isVisibleSecLoanApplication,
  SecLoansApplicationVisibleMetadata,
} from '@sec-loans-ui/common/types'
import { assertValue } from '@shared/helpers'
import {
  IfpApplicationMetadata,
  IfpApplicationState,
  LoanOfferMetadata,
  SecLoanApplicationState,
} from '@shared/sec-loans'
import { IntlShape } from 'react-intl'

export const getIfpTranslationForState = (
  formatMessage: IntlShape['formatMessage'],
  metadata: IfpApplicationMetadata,
  debtorUuid: string,
): string => {
  const { state, stateChangedBy, primaryUuid } = metadata
  switch (state) {
    case IfpApplicationState.Cancelled: {
      if (debtorUuid === stateChangedBy) {
        return formatMessage({ id: 'ifp-application-state-cancelled-by-user' })
      } else {
        return formatMessage({ id: 'ifp-application-state-cancelled-by-other-user' })
      }
    }

    case IfpApplicationState.Draft: {
      return formatMessage({ id: 'ifp-application-state-waiting-for-input' })
    }

    case IfpApplicationState.WaitingForCoDebtor: {
      const actionsNeeded = assertValue(metadata.actionsNeeded, 'metadata.actionsNeeded')
      if (actionsNeeded.includes(debtorUuid)) {
        return formatMessage({ id: 'ifp-application-state-waiting-for-user-approval' })
      } else if (actionsNeeded.length > 1) {
        return formatMessage(
          { id: 'ifp-application-state-waiting-for-many-other-approvals' },
          { numActionsNeeded: actionsNeeded.length },
        )
      } else {
        return formatMessage({ id: 'ifp-application-state-waiting-for-other-user-approval' })
      }
    }

    case IfpApplicationState.ReturnedByCoDebtor: {
      if (debtorUuid === primaryUuid) {
        return `${formatMessage({ id: 'ifp-application-state-waiting-for-input' })} ${formatMessage({ id: 'ifp-application-state-returned-by-other-user' })}`
      } else if (debtorUuid === stateChangedBy) {
        return formatMessage({ id: 'ifp-application-state-returned-by-user' })
      } else {
        return formatMessage({ id: 'ifp-application-state-returned-by-other-user' })
      }
    }

    case IfpApplicationState.Rejected: {
      return formatMessage({ id: 'ifp-application-state-rejected' })
    }

    case IfpApplicationState.Accepted: {
      return formatMessage({ id: 'ifp-application-state-accepted' })
    }

    case IfpApplicationState.Pending:
    case IfpApplicationState.Sending:
    case IfpApplicationState.WaitingForResend: {
      return formatMessage({ id: 'ifp-application-state-pending' })
    }

    case IfpApplicationState.UnavailableDraftInvalidated:
    case IfpApplicationState.UnavailableOther: {
      return formatMessage({ id: 'ifp-application-state-unavailable' })
    }
  }
}

const splitIfpApplications = (ifpApplications?: IfpApplicationMetadata[]) => {
  const inProgressIfpApplications: IfpApplicationMetadata[] = []
  const completedIfpApplications: IfpApplicationMetadata[] = []
  ifpApplications?.forEach((a) => {
    if (
      [
        IfpApplicationState.Cancelled,
        IfpApplicationState.Accepted,
        IfpApplicationState.Rejected,
        IfpApplicationState.UnavailableOther,
        IfpApplicationState.UnavailableDraftInvalidated,
      ].includes(a.state)
    ) {
      completedIfpApplications.push(a)
    } else {
      inProgressIfpApplications.push(a)
    }
  })

  return { inProgressIfpApplications, completedIfpApplications }
}

const splitLoanApplications = (loanApplications?: SecLoansApplicationVisibleMetadata[]) => {
  const inProgressLoanApplications: SecLoansApplicationVisibleMetadata[] = []
  const completedLoanApplications: SecLoansApplicationVisibleMetadata[] = []
  loanApplications?.forEach((a) => {
    if (
      [
        SecLoanApplicationState.CancelledByPrimaryApplicant,
        SecLoanApplicationState.CancelledBySecondaryApplicant,
        SecLoanApplicationState.Accepted,
        SecLoanApplicationState.Rejected,
      ].includes(a.applicationState)
    ) {
      completedLoanApplications.push(a)
    } else {
      inProgressLoanApplications.push(a)
    }
  })
  return { inProgressLoanApplications, completedLoanApplications }
}

const mergeAndSortApplications = (
  loanApplications: SecLoansApplicationVisibleMetadata[],
  ifpApplications: IfpApplicationMetadata[],
  loanOffers?: LoanOfferMetadata[],
): ApplicationDetails[] => {
  return [
    ...loanApplications.map(
      (applicationMetadata): ApplicationDetails => ({
        applicationType: ApplicationType.LoanApplication,
        applicationMetadata,
      }),
    ),
    ...ifpApplications.map(
      (applicationMetadata): ApplicationDetails => ({
        applicationType: ApplicationType.InstalmentFreePeriodApplication,
        applicationMetadata,
      }),
    ),
    ...(loanOffers?.map(
      (applicationMetadata): ApplicationDetails => ({
        applicationType: ApplicationType.LoanOffer,
        applicationMetadata,
      }),
    ) ?? []),
  ].sort((a, b) => a.applicationMetadata.updated - b.applicationMetadata.updated)
}

export const getApplicationDetails = ({
  ifpApplicationsMetadata,
  loanOffersMetadata,
  loanApplicationsMetadata,
}: ApplicationsMetaData): {
  inProgressApplications: ApplicationDetails[]
  completedApplications: ApplicationDetails[]
} => {
  const filteredLoanApplications = loanApplicationsMetadata?.filter((a) => isVisibleSecLoanApplication(a)) ?? []
  const { inProgressIfpApplications, completedIfpApplications } = splitIfpApplications(ifpApplicationsMetadata)
  const { inProgressLoanApplications, completedLoanApplications } = splitLoanApplications(filteredLoanApplications)

  const inProgressApplications = mergeAndSortApplications(inProgressLoanApplications, inProgressIfpApplications)
  const completedApplications = mergeAndSortApplications(
    completedLoanApplications,
    completedIfpApplications,
    loanOffersMetadata,
  )

  return { inProgressApplications, completedApplications }
}
